import type { IIssueType } from '@/assets/types/api/Leads';

// 表單

interface IForm {
  issueType: string;
  message: string;
  attachment?: File;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  relatedProductName: string;
}

// 表單錯誤訊息管理

interface IFormErrorMessage {
  issueType: string;
  message: string;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  relatedProductName: string;
}

const getForm = (): IForm => ({
  issueType: '',
  message: '',
  attachment: undefined,
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  relatedProductName: '',
});

const getFormErrorMessage = (): IFormErrorMessage => ({
  issueType: '',
  message: '',
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  relatedProductName: '',
});

export const useTechnicalSupportStore = defineStore(
  'technicalSupportStore',
  () => {
    const nuxtApp = useNuxtApp();

    /* State */

    const issueTypes = ref<IIssueType[]>([]);
    const form = ref<IForm>(getForm());
    const formErrorMessage = ref<IFormErrorMessage>(getFormErrorMessage());

    /* Actions */

    const USE_GET_ISSUE_TYPES = async () => {
      /**
       * 取得 Technical Support 的支援問題類型清單
       */
      const { $notification, $i18n } = nuxtApp;
      const { locale } = $i18n;

      return await useCustomFetch(
        () =>
          `/marketplace/api/v1/leads/issue-types/technical-support?websiteCode=${locale.value}`,
        {
          method: 'GET',
          lazy: true,
          immediate: true,
          onResponse: ({ response }) => {
            const { _data, status } = response;

            if (status >= 200 && status < 300) {
              issueTypes.value = [...(_data as IIssueType[])];
            } else {
              console.error(_data);
              $notification({
                message: (_data as { message: string }).message,
                type: 'error',
              });
            }
          },
        }
      );
    };

    const POST_TECHNICAL_SUPPORT = async (
      formData: FormData
    ): Promise<void> => {
      /**
       * 送出 Contact Us 表單
       */
      const { $i18n, $customFetch, $notification } = nuxtApp;
      const { locale } = $i18n;

      try {
        await $customFetch(
          `/marketplace/api/v1/leads/contact-us?websiteCode=${locale.value}`,
          { method: 'POST', body: formData }
        );
      } catch (error) {
        const message = (error as { message: string }).message || '';
        $notification({ message, type: 'error' });

        if (message === 'The Phone field is not a valid phone number.') {
          form.value.phoneNumber = '';
        }
        return Promise.reject(error);
      }
    };

    const FILL_FORM_WITH_PROFILE = (): void => {
      /**
       * 用戶已登入時表單自動代入資料
       */

      const profileStore = useProfileStore();
      const { profile } = storeToRefs(profileStore);

      if (!profile.value.isLogin) return;

      form.value.firstName = profile.value.firstName ?? '';
      form.value.lastName = profile.value.lastName ?? '';
      form.value.email = profile.value.email ?? '';
      form.value.phoneCode = profile.value.mobileCountryCode ?? '';
      form.value.phoneNumber = profile.value.mobileNumber ?? '';
      form.value.companyName = profile.value.companyName ?? '';
      form.value.country = profile.value.country ?? '';
    };

    const VALID_FORM_ISSUE_TYPE = (): boolean => {
      /**
       * 驗證 issueType 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.issueType === '') {
        formErrorMessage.value.issueType = t('form.error-required-message');
        return false;
      }

      formErrorMessage.value.issueType = '';
      return true;
    };

    const VALID_FORM_RELATED_PRODUCT_NAME = (): boolean => {
      /**
       * 驗證 relatedProductName 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      // const { $i18n } = nuxtApp;
      // const { t } = $i18n;

      formErrorMessage.value.relatedProductName = '';
      return true;
    };

    const VALID_FORM_FIRST_NAME = (): boolean => {
      /**
       * 驗證 firstName 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.firstName === '') {
        formErrorMessage.value.firstName = t('form.error-required-message');
        return false;
      }

      formErrorMessage.value.firstName = '';
      return true;
    };

    const VALID_FORM_LAST_NAME = (): boolean => {
      /**
       * 驗證 lastName 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.lastName === '') {
        formErrorMessage.value.lastName = t('form.error-required-message');
        return false;
      }

      formErrorMessage.value.lastName = '';
      return true;
    };

    const VALID_FORM_EMAIL = (): boolean => {
      /**
       * 驗證 email 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.email === '') {
        formErrorMessage.value.email = t('form.error-required-message');
        return false;
      }

      if (!validateCompanyEmail(form.value.email)) {
        formErrorMessage.value.email = t('form.error-email-message');
        return false;
      }

      formErrorMessage.value.email = '';
      return true;
    };

    const VALID_FORM_PHONE_CODE = (): boolean => {
      /**
       * 驗證 phoneCode 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.phoneCode === '') {
        formErrorMessage.value.phoneCode = t('form.error-required-message');
        return false;
      }

      formErrorMessage.value.phoneCode = '';
      return true;
    };

    const VALID_FORM_PHONE_NUMBER = (): boolean => {
      /**
       * 驗證 phoneNumber 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.phoneNumber === '') {
        formErrorMessage.value.phoneNumber = t('form.error-required-message');
        return false;
      }

      const isNumeric = /^\d+$/.test(form.value.phoneNumber);
      if (!isNumeric) {
        formErrorMessage.value.phoneNumber = t('form.error-numeric-only');
        return false;
      }

      formErrorMessage.value.phoneNumber = '';
      return true;
    };

    const VALID_FORM_COMPANY_NAME = (): boolean => {
      /**
       * 驗證 companyName 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.companyName === '') {
        formErrorMessage.value.companyName = t('form.error-required-message');
        return false;
      }

      formErrorMessage.value.companyName = '';
      return true;
    };

    const VALID_FORM_COUNTRY = (): boolean => {
      /**
       * 驗證 country 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.country === '') {
        formErrorMessage.value.country = t('form.error-required-message');
        return false;
      }

      formErrorMessage.value.country = '';
      return true;
    };

    const VALID_FORM_MESSAGE = (): boolean => {
      /**
       * 驗證 message 欄位，根據錯誤類型設定 ErrorMessage 文字
       * 並回傳驗證狀態（驗證通過回傳 True）
       */
      const { $i18n } = nuxtApp;
      const { t } = $i18n;

      if (form.value.message === '') {
        formErrorMessage.value.message = t('form.error-required-message');
        return false;
      }

      formErrorMessage.value.message = '';
      return true;
    };

    const VALID_FORM = (): boolean => {
      /**
       * 驗證所有欄位（驗證通過回傳 True）
       */

      const result = [
        VALID_FORM_ISSUE_TYPE(),
        VALID_FORM_RELATED_PRODUCT_NAME(),
        VALID_FORM_FIRST_NAME(),
        VALID_FORM_LAST_NAME(),
        VALID_FORM_EMAIL(),
        VALID_FORM_PHONE_CODE(),
        VALID_FORM_PHONE_NUMBER(),
        VALID_FORM_COMPANY_NAME(),
        VALID_FORM_COUNTRY(),
        VALID_FORM_MESSAGE(),
      ];

      return result.every((value) => value === true);
    };

    const RESET_FORM = (): void => {
      /**
       * 重置表單
       */

      form.value = getForm();
      formErrorMessage.value = getFormErrorMessage();
    };

    return {
      issueTypes,
      form,
      formErrorMessage,
      USE_GET_ISSUE_TYPES,
      POST_TECHNICAL_SUPPORT,
      FILL_FORM_WITH_PROFILE,
      VALID_FORM_ISSUE_TYPE,
      VALID_FORM_RELATED_PRODUCT_NAME,
      VALID_FORM_FIRST_NAME,
      VALID_FORM_LAST_NAME,
      VALID_FORM_EMAIL,
      VALID_FORM_PHONE_CODE,
      VALID_FORM_PHONE_NUMBER,
      VALID_FORM_COMPANY_NAME,
      VALID_FORM_COUNTRY,
      VALID_FORM_MESSAGE,
      VALID_FORM,
      RESET_FORM,
    };
  }
);
